class AccessibilityWidget {
    constructor() {
        this.trigger = document.getElementById('ada-trigger');
        this.panel = document.getElementById('ada-panel');
        this.closeBtn = document.getElementById('ada-close');
        this.readingGuide = document.getElementById('reading-guide');
        
        this.root = document.documentElement;
        this.body = document.body;
        
        this.settings = {
            fontSize: 0,
            spacing: 0,
            dyslexic: false,
            contrast: 'none',
            colorBlind: 'none',
            cursor: false,
            guide: false,
            hideImages: false,
            highlightHeadings: false,
            highlightLinks: false,
            tts: false
        };

        this.synth = window.speechSynthesis;
        this.hoverTimeout = null;

        if(this.trigger) this.init();
    }

    init() {
        this.loadSettings();
        
        this.trigger.addEventListener('click', () => this.openPanel());
        this.closeBtn.addEventListener('click', () => this.closePanel());
        
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.panel.classList.contains('is-open')) {
                this.closePanel();
            }
        });

        document.addEventListener('click', (e) => {
            const isOpen = this.panel.classList.contains('is-open');
            const isInsidePanel = this.panel.contains(e.target);
            const isTrigger = this.trigger.contains(e.target);

            if (isOpen && !isInsidePanel && !isTrigger) {
                this.closePanel();
            }
        });

        document.addEventListener('mousemove', (e) => {
            if (this.settings.guide) {
                this.readingGuide.style.top = e.clientY + 'px';
            }
        });

        this.setupTTS();
    }

    setupTTS() {
        const textElements = document.querySelectorAll('p, h1, h2, h3, a, button, span, .rte');
        textElements.forEach(el => {
            el.addEventListener('mouseenter', (e) => {
                if (!this.settings.tts) return;
                
                this.hoverTimeout = setTimeout(() => {
                    this.speak(e.target.innerText || e.target.getAttribute('aria-label'));
                    e.target.style.outline = '2px solid orange';
                }, 500);
            });

            el.addEventListener('mouseleave', (e) => {
                if (!this.settings.tts) return;
                clearTimeout(this.hoverTimeout);
                this.synth.cancel();
                e.target.style.outline = '';
            });
        });
    }

    speak(text) {
        if (!text) return;
        this.synth.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        this.synth.speak(utterance);
    }

    openPanel() {
        this.panel.classList.add('is-open');
        this.trigger.setAttribute('aria-expanded', 'true');
        this.closeBtn.focus();
        this.body.style.overflow = 'hidden';
    }

    closePanel() {
        this.panel.classList.remove('is-open');
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.focus();
        this.body.style.overflow = '';
    }

    saveSettings() {
        localStorage.setItem('shopify_ada_settings', JSON.stringify(this.settings));
        this.updateVisuals(); 
    }

    loadSettings() {
        const saved = localStorage.getItem('shopify_ada_settings');
        if (saved) {
            this.settings = JSON.parse(saved);
        }
        this.updateVisuals();
        this.syncUI();
    }

    updateVisuals() {
        this.root.classList.remove(
            'accessibility-mode-dyslexic',
            'accessibility-mode-spacing-medium',
            'accessibility-mode-spacing-large',
            'accessibility-mode-cursor-big',
            'accessibility-mode-hide-images',
            'accessibility-highlight-headings',
            'accessibility-highlight-links',
            'ada-filter-grayscale',
            'ada-filter-invert',
            'ada-filter-high-contrast',
            'ada-cb-protanopia',
            'ada-cb-deuteranopia',
            'ada-cb-tritanopia',
            'ada-cb-grayscale'
        );
        
        this.root.style.fontSize = '';
        this.readingGuide.style.display = 'none';

        const headerImage = document.querySelector('.header-secondary-image');
        if (headerImage) {
            headerImage.style.display = ''; 
        }

        if (this.settings.fontSize === 1) {
            this.root.style.fontSize = this.root.style.fontSize = 'calc(100% - 2px)'; 
            
            const isMobile = window.matchMedia('(max-width: 768px)').matches;
            
            // 3. Only hide the image if it exists AND we are on mobile
            if (headerImage && isMobile) {
                headerImage.style.display = 'none';
            }
        }
        // if (this.settings.fontSize === 1) this.root.style.fontSize = (parseFloat(getComputedStyle(document.body).fontSize) - 2) + 'px';

        if (this.settings.dyslexic) this.root.classList.add('accessibility-mode-dyslexic');
        if (this.settings.spacing === 1) this.root.classList.add('accessibility-mode-spacing-medium');
        if (this.settings.spacing === 2) this.root.classList.add('accessibility-mode-spacing-large');
        if (this.settings.cursor) this.root.classList.add('accessibility-mode-cursor-big');
        if (this.settings.hideImages) this.root.classList.add('accessibility-mode-hide-images');
        if (this.settings.highlightHeadings) this.root.classList.add('accessibility-highlight-headings');
        if (this.settings.highlightLinks) this.root.classList.add('accessibility-highlight-links');
        if (this.settings.guide) this.readingGuide.style.display = 'block';

        if (this.settings.contrast === 'high') {
            this.root.classList.add('ada-filter-high-contrast');
        } else if (this.settings.contrast === 'invert') {
            this.root.classList.add('ada-filter-invert');
        }

        if (this.settings.colorBlind === 'protanopia') this.root.classList.add('ada-cb-protanopia');
        if (this.settings.colorBlind === 'deuteranopia') this.root.classList.add('ada-cb-deuteranopia');
        if (this.settings.colorBlind === 'tritanopia') this.root.classList.add('ada-cb-tritanopia');
        if (this.settings.colorBlind === 'grayscale') this.root.classList.add('ada-cb-grayscale');
    }

    syncUI() {
        if(!document.getElementById('ada-dyslexic')) return;
        
        document.getElementById('ada-dyslexic').checked = this.settings.dyslexic;
        document.getElementById('ada-cursor').checked = this.settings.cursor;
        document.getElementById('ada-guide').checked = this.settings.guide;
        document.getElementById('ada-imgs').checked = this.settings.hideImages;
        document.getElementById('ada-highlight-links').checked = this.settings.highlightLinks;
        document.getElementById('ada-highlight-titles').checked = this.settings.highlightHeadings;
        document.getElementById('ada-tts').checked = this.settings.tts;
        document.getElementById('ada-colorblind-select').value = this.settings.colorBlind;
    }

    setFontSize(level) {
        this.settings.fontSize = level;
        this.saveSettings();
    }

    setSpacing(level) {
        this.settings.spacing = level;
        this.saveSettings();
    }

    toggleDyslexic(active) {
        this.settings.dyslexic = active;
        this.saveSettings();
    }

    toggleHighlightHeadings(active) {
        this.settings.highlightHeadings = active;
        this.saveSettings();
    }
    
    toggleHighlightLinks(active) {
        this.settings.highlightLinks = active;
        this.saveSettings();
    }

    setContrast(mode) {
        this.settings.contrast = mode;
        this.saveSettings();
    }

    setColorBlindness(type) {
        this.settings.colorBlind = type;
        this.saveSettings();
    }

    toggleBigCursor(active) {
        this.settings.cursor = active;
        this.saveSettings();
    }

    toggleReadingGuide(active) {
        this.settings.guide = active;
        this.saveSettings();
    }

    toggleHideImages(active) {
        this.settings.hideImages = active;
        this.saveSettings();
    }

    toggleTTS(active) {
        this.settings.tts = active;
        if (!active) this.synth.cancel();
        this.saveSettings();
    }

    resetAll() {
        localStorage.removeItem('shopify_ada_settings');
        this.settings = {
            fontSize: 0,
            spacing: 0,
            dyslexic: false,
            contrast: 'none',
            colorBlind: 'none',
            cursor: false,
            guide: false,
            hideImages: false,
            highlightHeadings: false,
            highlightLinks: false,
            tts: false
        };
        this.updateVisuals();
        this.syncUI();
    }
}

document.addEventListener('DOMContentLoaded', () => {
    window.adaWidget = new AccessibilityWidget();
});